import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { getDisplayName } from '../utils/displayHelpers';
import { 
  Star, 
  MapPin, 
  Clock, 
  Shield, 
  Heart, 
  Calendar,
  MessageCircle,
  Phone,
  Mail,
  Users,
  CheckCircle
} from 'lucide-react';
import ReviewCard from '../components/ReviewCard';

const CaregiverProfile = () => {
  const { id } = useParams();
  const { caregivers, reviews } = useData();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  const caregiver = caregivers.find(c => c.id === id);
  const caregiverReviews = reviews.filter(r => r.toCaregiverId === id);

  if (!caregiver) {
    return (
      <div className="pf-page-wrap flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Caregiver Not Found</h1>
          <p className="text-gray-600 mb-4">The caregiver you're looking for doesn't exist.</p>
          <Link to="/search" className="btn-primary">Browse Caregivers</Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'photos', label: 'Photos' },
    { id: 'reviews', label: `Reviews (${caregiverReviews.length})` },
    { id: 'availability', label: 'Availability' }
  ];

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${
          i < Math.floor(rating) ? 'text-yellow-500 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="card mb-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Profile Image */}
            <div className="flex-shrink-0">
              <img
                src={caregiver.user.avatar}
                alt={getDisplayName(caregiver.user)}
                className="w-32 h-32 rounded-full object-cover mx-auto lg:mx-0"
              />
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {getDisplayName(caregiver.user)}
                  </h1>
                  <p className="text-xl text-gray-600 mb-4">{caregiver.title}</p>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-1">
                      {renderStars(caregiver.rating)}
                      <span className="ml-2 text-lg font-semibold">{caregiver.rating}</span>
                      <span className="text-gray-500">({caregiver.reviewCount} reviews)</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {caregiver.badges.map((badge) => (
                      <span key={badge} className="badge badge-success">
                        {badge === 'verified' ? 'Active' : 
                         badge === 'vetChecked' ? 'Pet First Aid' :
                         badge === 'superhost' ? 'Top Rated' :
                         badge === 'premium' ? 'Premium' :
                         badge === 'catSpecialist' ? 'Cat Specialist' : badge}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  {user && user.role === 'owner' && (
                    <>
                      <Link
                        to={`/owner-booking/${caregiver.id}`}
                        className="btn-primary text-center"
                      >
                        Book Now
                      </Link>
                      <button className="btn-secondary flex items-center justify-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        Message
                      </button>
                    </>
                  )}
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed">{caregiver.bio}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="card mb-6">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-[var(--pf-accent)] text-[var(--pf-accent)]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Tab Content */}
            <div className="card">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Services Offered</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <Heart className="w-5 h-5 text-[var(--pf-accent)]" />
                        <span>Pet Sitting</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-[var(--pf-accent)]" />
                        <span>Hourly Care</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-[var(--pf-accent)]" />
                        <span>Daily Care</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-[var(--pf-accent)]" />
                        <span>Overnight Stays</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Species Accepted</h3>
                    <div className="flex gap-2">
                      {caregiver.speciesAccepted.map((species) => (
                        <span key={species} className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--pf-accent-soft)] text-[var(--pf-accent)]">
                          {species}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Capacity</h3>
                    <p className="text-gray-700">
                      Can care for up to {caregiver.maxPets} pet{caregiver.maxPets !== 1 ? 's' : ''} at a time
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'photos' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Home Photos</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {caregiver.homePhotos.map((photo, index) => (
                      <img
                        key={index}
                        src={photo}
                        alt={`Caregiver home view ${index + 1}`}
                        className="w-full h-64 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'reviews' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Reviews</h3>
                  {caregiverReviews.length > 0 ? (
                    <div className="space-y-4">
                      {caregiverReviews.map((review) => (
                        <ReviewCard key={review.id} review={review} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Star className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No reviews yet</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'availability' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Availability</h3>
                  <div className="space-y-3">
                    {caregiver.calendarAvailability.map((slot, index) => (
                      <div key={index} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {new Date(slot.from).toLocaleDateString()}
                            </p>
                            <p className="text-sm text-gray-600">
                              {new Date(slot.from).toLocaleTimeString()} - {new Date(slot.to).toLocaleTimeString()}
                            </p>
                          </div>
                          <span className="badge badge-success">Available</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Hourly Rate</span>
                  <span className="font-semibold">₹{caregiver.hourlyRate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Daily Rate</span>
                  <span className="font-semibold">₹{caregiver.dailyRate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Monthly Rate</span>
                  <span className="font-semibold">₹{caregiver.monthlyRate}</span>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Contact via platform</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Active on platform</span>
                </div>
              </div>
            </div>

            {/* Platform Features */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Features</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-green-600" />
                  <span className="text-sm">Community reviews available</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm">Profile complete</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm">Direct messaging available</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaregiverProfile;
